import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import redCrossIcon from '../../../static/icons/red-cross.svg';
import CheckboxBordered from '../../CheckboxBordered';

const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Select all that apply',
  getOptionLabel = (opt) => {
    if (opt != null && typeof opt === 'object') {
      const label =
        opt.label ??
        opt.subjectName ??
        opt.name ??
        opt.title ??
        opt.subjectCode ??
        opt.code ??
        opt.value ??
        '';
      return String(label ?? '');
    }
    return String(opt ?? '');
  },
  getOptionValue = (opt) => {
    if (opt != null && typeof opt === 'object') {
      return opt.value ?? opt.code ?? opt.subjectCode ?? JSON.stringify(opt);
    }
    return opt;
  },
  disabled = false,
  alwaysWhite = false,
  showCheckboxes = false,
  summaryLimit = 2,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const safeOptions = Array.isArray(options)
    ? options.filter((opt) => opt != null)
    : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isValueSelected = (val) =>
    selectedValues.some((v) => String(v ?? '') === String(val ?? ''));

  const availableOptions = safeOptions.filter(
    (opt) => !isValueSelected(getOptionValue(opt)),
  );

  const handleSelect = (option) => {
    const value = getOptionValue(option);
    if (!isValueSelected(value)) {
      onChange([...selectedValues, value]);
    }
    if (!showCheckboxes) setIsOpen(false);
  };

  const handleToggleOption = (option, e) => {
    e?.stopPropagation?.();
    const value = getOptionValue(option);
    if (isValueSelected(value)) {
      onChange(
        selectedValues.filter((v) => String(v ?? '') !== String(value ?? '')),
      );
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleRemove = (valueToRemove, e) => {
    e.stopPropagation();
    onChange(
      selectedValues.filter(
        (v) => String(v ?? '') !== String(valueToRemove ?? ''),
      ),
    );
  };

  const getDisplayLabel = (value) => {
    if (value != null && typeof value === 'object') {
      return String(
        value.label ??
          value.subjectName ??
          value.name ??
          value.title ??
          value.subjectCode ??
          value.value ??
          '',
      );
    }
    const option = safeOptions.find((opt) => {
      const optVal = getOptionValue(opt);
      return optVal === value || String(optVal ?? '') === String(value ?? '');
    });
    if (option != null) return getOptionLabel(option);
    return getOptionLabel(value);
  };

  const summaryText = useMemo(() => {
    if (selectedValues.length === 0) return placeholder;
    const labels = selectedValues.map(getDisplayLabel);
    if (labels.length <= summaryLimit) return labels.join(', ');
    const visible = labels.slice(0, summaryLimit).join(', ');
    return `${visible}... +${labels.length - summaryLimit} more`;
  }, [selectedValues, placeholder, summaryLimit, safeOptions]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const listOptions = showCheckboxes ? safeOptions : availableOptions;

  return (
    <Container ref={containerRef}>
      <SelectionBox
        onClick={handleToggle}
        $disabled={disabled}
        $alwaysWhite={alwaysWhite}
        $active={isOpen || selectedValues.length > 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={placeholder}
      >
        <SelectTrigger $hasValue={selectedValues.length > 0}>
          <span>{showCheckboxes ? summaryText : placeholder}</span>
          <DropdownArrow $isOpen={isOpen} />
        </SelectTrigger>
        {!showCheckboxes && selectedValues.length > 0 && (
          <ChipsContainer>
            {selectedValues.map((value, index) => (
              <Chip key={`${value}-${index}`}>
                {getDisplayLabel(value)}
                <ChipRemoveIcon
                  src={redCrossIcon}
                  onClick={(e) => handleRemove(value, e)}
                  role="button"
                  aria-label={`Remove ${getDisplayLabel(value)}`}
                />
              </Chip>
            ))}
          </ChipsContainer>
        )}
      </SelectionBox>
      {isOpen && (
        <DropdownList role="listbox">
          {listOptions.length > 0 ? (
            listOptions.map((option, index) => {
              const value = getOptionValue(option);
              const checked = isValueSelected(value);
              return (
                <DropdownItem
                  key={`${value}-${index}`}
                  $checked={checked}
                  onClick={(e) =>
                    showCheckboxes
                      ? handleToggleOption(option, e)
                      : handleSelect(option)
                  }
                >
                  {showCheckboxes && (
                    <CheckboxBordered
                      checked={checked}
                      onChange={(e) => handleToggleOption(option, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  {getOptionLabel(option)}
                </DropdownItem>
              );
            })
          ) : (
            <NoOptions>
              {showCheckboxes ? 'No students available' : 'No more options available'}
            </NoOptions>
          )}
        </DropdownList>
      )}
    </Container>
  );
};

export default MultiSelectDropdown;

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SelectionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border: 1.5px solid
    ${({ $active }) => ($active ? '#7200e0' : '#d1d5db')};
  border-radius: 8px;
  background: ${({ $disabled, $alwaysWhite }) =>
    $disabled && !$alwaysWhite
      ? 'var(--color-neutral-80, #F2F1F3)'
      : '#FFFFFF'};
  min-height: 44px;
  cursor: pointer;
  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const SelectTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  color: ${({ $hasValue }) => ($hasValue ? '#1e252a' : '#9ca3af')};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DropdownArrow = styled.span`
  border: solid #6b7280;
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 3px;
  flex-shrink: 0;
  transform: ${(props) =>
    props.$isOpen ? 'rotate(-135deg)' : 'rotate(45deg)'};
  transition: transform 0.2s;
  margin-top: ${(props) => (props.$isOpen ? '4px' : '-2px')};
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 280px;
  overflow-y: auto;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  background: ${({ $checked }) => ($checked ? '#f1e6fc' : '#ffffff')};
  transition: background-color 0.15s;

  &:hover {
    background: #f1e6fc;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:only-child {
    border-radius: 8px;
  }
`;

const NoOptions = styled.div`
  padding: 10px 14px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  color: #9ca3af;
  text-align: center;
  background-color: #ffffff;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 16px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 13px;
  font-weight: 400;
  background-color: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
`;

const ChipRemoveIcon = styled.img`
  width: 14px;
  height: 14px;
  cursor: pointer;
  padding: 2px;
  border: 1px solid #e2483d;
  border-radius: 50%;
`;
